import {
    Box, Typography, Chip,
    useTheme, alpha,
} from '@mui/material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { Article as ArticleIcon } from '@mui/icons-material';
import ChartCard from './ChartCard';

const PLATFORM_ICON = {
    youtube: YouTubeIcon,
    instagram: InstagramIcon,
    facebook: FacebookIcon,
};
const PLATFORM_COLOR = {
    youtube: '#FF0000',
    instagram: '#E1306C',
    facebook: '#1877F2',
};

function StatBadge({ icon: Icon, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Icon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.72rem' }}>
                {Number(value) >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
            </Typography>
        </Box>
    );
}

const TopPostsList = ({ posts = [], viral = false, title, icon, onPostClick }) => {
    const theme = useTheme();
    if (!posts.length) return null;

    return (
        <ChartCard
            title={title || (viral ? '🔥 Viral Posts' : 'Top Performing Posts')}
            icon={icon || <ArticleIcon />}
            height="auto"
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                {posts.map((post, idx) => {
                    const PlatIcon = PLATFORM_ICON[post.platform];
                    const platColor = PLATFORM_COLOR[post.platform] || '#6366f1';

                    return (
                        <Box
                            key={post.id || idx}
                            onClick={() => onPostClick && post.id && onPostClick(post.id)}
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'center',
                                p: 1.5,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                transition: 'background 0.15s',
                                cursor: onPostClick ? 'pointer' : 'default',
                                '&:hover': onPostClick ? { bgcolor: alpha(platColor, 0.06), borderColor: alpha(platColor, 0.3) } : {},
                            }}
                        >
                            {/* Rank number */}
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '1rem',
                                    color: idx < 3 ? platColor : 'text.disabled',
                                    minWidth: 22,
                                    textAlign: 'center',
                                }}
                            >
                                {idx + 1}
                            </Typography>

                            {/* Thumbnail */}
                            {post.media_url ? (
                                <Box
                                    component="img"
                                    src={post.media_url}
                                    alt=""
                                    sx={{
                                        width: 64,
                                        height: 44,
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                        flexShrink: 0,
                                    }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            ) : (
                                <Box sx={{ width: 64, height: 44, bgcolor: alpha(platColor, 0.1), borderRadius: '4px', flexShrink: 0 }} />
                            )}

                            {/* Title + meta */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
                                    {PlatIcon && <PlatIcon sx={{ fontSize: 13, color: platColor }} />}
                                    {post.media_type && (
                                        <Chip
                                            label={post.media_type}
                                            size="small"
                                            sx={{ height: 16, fontSize: '0.65rem', fontWeight: 700, px: 0.5 }}
                                        />
                                    )}
                                    {viral && (
                                        <Chip
                                            icon={<LocalFireDepartmentIcon sx={{ fontSize: 11 }} />}
                                            label={`${post.engagement_rate}%`}
                                            size="small"
                                            sx={{ height: 16, fontSize: '0.65rem', fontWeight: 800, bgcolor: alpha('#f97316', 0.12), color: '#f97316', px: 0.5 }}
                                        />
                                    )}
                                    {post.published_at && (
                                        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: '0.68rem', flexShrink: 0 }}>
                                            {new Date(post.published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        fontSize: '0.82rem',
                                        color: post.title ? 'text.primary' : 'text.disabled',
                                    }}
                                >
                                    {post.title || '(no title)'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                                    <StatBadge icon={VisibilityIcon} value={post.views} />
                                    <StatBadge icon={ThumbUpIcon} value={post.likes} />
                                    <StatBadge icon={CommentIcon} value={post.comments} />
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </ChartCard>
    );
};

export default TopPostsList;
